import { Router } from '@components/Router'
import { ThemeProvider } from '@components/providers/ThemeProvider'
import { WalletProvider } from '@components/providers/WalletProvider'
import { Toaster } from '@components/ui/Toaster'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { QueryClient } from '@tanstack/react-query'
import {
  PersistQueryClientProvider,
  type PersistedClient,
} from '@tanstack/react-query-persist-client'
import { Amplify } from 'aws-amplify'
import { Suspense, lazy } from 'react'

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
    },
  },
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // グローバルデフォルト設定 - キャッシュを最大限活用
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // ネットワークモードをオフライン優先に
      networkMode: 'offlineFirst',
    },
  },
})

// BigIntをサポートするカスタムシリアライズ関数
const serializeWithBigInt = (value: unknown): string => {
  return JSON.stringify(value, (_key: string, val: unknown) =>
    typeof val === 'bigint' ? `${val.toString()}n` : val
  )
}

const deserializeWithBigInt = (value: string): PersistedClient => {
  return JSON.parse(value, (_key: string, val: unknown) => {
    // BigIntの復元
    if (typeof val === 'string' && /^\d+n$/.test(val)) {
      return BigInt(val.slice(0, -1))
    }
    // Dateオブジェクトの復元（ISO文字列の場合）
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)) {
      return new Date(val)
    }
    return val
  }) as PersistedClient
}

const persister = createAsyncStoragePersister({
  storage: window.localStorage,
  // localStorageのキープレフィックス
  key: 'arcs-wallet-cache',
  // BigIntをサポートするシリアライズ/デシリアライズ関数
  serialize: serializeWithBigInt,
  deserialize: deserializeWithBigInt,
})

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(async () => {
      const mod = await import('@tanstack/react-query-devtools')
      return { default: mod.ReactQueryDevtools }
    })
  : ((() => null) as React.ComponentType<{ initialIsOpen?: boolean }>)

function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        // キャッシュの有効期限（7日間）
        maxAge: 7 * 24 * 60 * 60 * 1000,
        // ハイドレーション時に古いデータを破棄するかどうか
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            // すべての成功したクエリを保存（dataの有無に関わらず）
            return query.state.status === 'success'
          },
        },
      }}
    >
      <ThemeProvider>
        <WalletProvider>
          <Router />
          <Toaster />
        </WalletProvider>
      </ThemeProvider>
      {import.meta.env.DEV ? (
        <Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </Suspense>
      ) : null}
    </PersistQueryClientProvider>
  )
}

export default App
